import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { formatMoney } from '@sebastian/shared';
import type { RestaurantSettings } from '@sebastian/shared';

/**
 * The currency symbol is a restaurant setting, so every price on screen reads
 * it from one place rather than hard-coding "$".
 */
const CurrencyContext = createContext<{ symbol: string; decimals: boolean }>({
  symbol: '$',
  decimals: true,
});

export function CurrencyProvider({
  settings,
  children,
}: {
  settings: RestaurantSettings | undefined;
  children: ReactNode;
}) {
  const value = useMemo(
    () => ({ symbol: settings?.currencySymbol ?? '$', decimals: settings?.currencyDecimals ?? true }),
    [settings?.currencySymbol, settings?.currencyDecimals],
  );
  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

export function useCurrency() {
  const { symbol, decimals } = useContext(CurrencyContext);
  return {
    symbol,
    decimals,
    money: (cents: number) => formatMoney(cents, symbol, decimals),
  };
}

/** Inline price, right-aligned and tabular so columns of figures line up. */
export function Money({ cents, className }: { cents: number; className?: string }) {
  const { money } = useCurrency();
  return <span className={`tnum ${className ?? ''}`}>{money(cents)}</span>;
}
